// Auto-generated by splitter.py — do not regenerate; edit directly.

import React, { useState, useEffect } from 'react';
import { AlertTriangle, Download } from 'lucide-react';
import { BANK_FILE_FORMATS } from '../../../constants/finance';
import { previewReportDraft } from '../../../lib/backOfficeApi';
import { toast } from '../../../lib/buses';
import { StatusDot } from '../../../ui/Chip';
import { Field, Input, Select, ToggleControl } from '../../../ui/Form';
import { Modal } from '../../../ui/Modal';
import { WizardFooter, WizardSteps } from '../../../ui/Wizard';
import { shortId } from '../../../lib/format';
import { IdTag } from '../../../ui/IdTag';
import { BRAND } from '../../../lib/brand';

// Provider reconciliation targets — each carries the rail and (where the rail
// is shared, e.g. mobile money) the network to disambiguate MTN vs Airtel.
const RECON_PROVIDERS = [
  { key: 'mtn',    label: 'MTN Mobile Money', rail: 'mobilemoney', network: 'MTN' },
  { key: 'airtel', label: 'Airtel Money',     rail: 'mobilemoney', network: 'Airtel' },
  { key: 'mpesa',  label: 'M-Pesa',           rail: 'mpesa' },
  { key: 'card',   label: 'Card',             rail: 'card' },
];

// Columns pulled from the report engine's transactions source, in this exact
// order — viz.rows come back as arrays aligned to these names.
const RECON_COLUMNS = ['created', 'phone', 'customer', 'network', 'amount', 'currency', 'reference', 'txn_id', 'state'];
const isoDay = (offsetDays = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
};

// Real SHA-256 of the exact bytes that get downloaded — hex digest.
async function sha256Hex(text) {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
}

export const BankFileWizardModal = ({ onClose, availablePayouts = [], onReconExport }) => {
  const [source, setSource] = useState('payouts'); // 'payouts' | 'reconciliation'
  const [step, setStep] = useState('select');
  const [formatId, setFormatId] = useState('stanbic-eft');
  const [selectedIds, setSelectedIds] = useState(new Set(availablePayouts.filter(p => p.stage === 'Pending approval' || p.stage === 'Processing').slice(0, 12).map(p => p.id)));
  const [valueDate, setValueDate] = useState('');
  const [batchRef, setBatchRef] = useState('BAT-' + new Date().toISOString().slice(0, 10).replace(/-/g, '') + '-' + String(Math.floor(Math.random() * 900 + 100)));
  const [memo, setMemo] = useState('');
  const [includeFees, setIncludeFees] = useState(false);
  const [requireDualApproval, setRequireDualApproval] = useState(true);
  const [approver, setApprover] = useState('');
  const [encryption, setEncryption] = useState('pgp');
  const [checksum, setChecksum] = useState('');

  // Provider-reconciliation source state — a line-level export of every
  // transaction for ONE provider over a date window, to reconcile against the
  // provider's settlement statement.
  const [from, setFrom] = useState(() => isoDay(-30));
  const [to, setTo] = useState(() => isoDay(0));
  const [providerKey, setProviderKey] = useState('mtn');
  const [reconStatus, setReconStatus] = useState('successful'); // 'successful' | 'all'
  const [reconRows, setReconRows] = useState([]); // mapped preview rows [{date,msisdn,customer,amount,currency,tkt_ref,gateway_ref,status}]
  const [reconLoading, setReconLoading] = useState(false);
  const [reconError, setReconError] = useState('');

  const provider = RECON_PROVIDERS.find(p => p.key === providerKey) || RECON_PROVIDERS[0];

  // The shared report-engine draft for this provider/window/status — used for
  // BOTH the preview (capped) and the export (full query, server-streamed).
  const reconFilters = [
    { col: 'rail', op: 'eq', val: provider.rail },
    ...(provider.network ? [{ col: 'network', op: 'eq', val: provider.network }] : []),
    ...(reconStatus !== 'all' ? [{ col: 'state', op: 'eq', val: reconStatus }] : []),
  ];

  // Preview the line-level reconciliation rows through the report engine whenever
  // the provider/window/status change. The engine caps the preview (~1000 rows);
  // the export below runs the FULL query.
  useEffect(() => {
    if (source !== 'reconciliation') return;
    if (!from || !to) return;
    const ac = new AbortController();
    setReconLoading(true);
    setReconError('');
    previewReportDraft(
      { source: 'transactions', columns: RECON_COLUMNS, filters: reconFilters, chartType: 'table', dateFrom: from, dateTo: to },
      { signal: ac.signal },
    )
      .then((viz) => {
        if (ac.signal.aborted) return;
        const cols = viz?.columns || RECON_COLUMNS;
        const idx = (name) => cols.indexOf(name);
        const iCreated = idx('created'), iPhone = idx('phone'), iCustomer = idx('customer');
        const iAmount = idx('amount'), iCurrency = idx('currency'), iReference = idx('reference');
        const iTxn = idx('txn_id'), iState = idx('state');
        const rows = (viz?.rows || []).map((row) => ({
          date: row[iCreated],
          msisdn: row[iPhone],
          customer: row[iCustomer],
          amount: Number(row[iAmount]) || 0,
          currency: row[iCurrency],
          tkt_ref: row[iReference],
          gateway_ref: row[iTxn],
          status: row[iState],
        }));
        setReconRows(rows);
        setReconLoading(false);
      })
      .catch((e) => {
        if (ac.signal.aborted) return;
        setReconRows([]);
        setReconError('Could not load transactions for this provider and window.');
        setReconLoading(false);
      });
    return () => ac.abort();
  }, [source, providerKey, from, to, reconStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const format = BANK_FILE_FORMATS.find(f => f.id === formatId);

  // Payouts: per-row checkbox selection drives the batch. Reconciliation: the
  // whole fetched result IS the export — no selection, totals come from the API.
  // `currency` carried through from the real payout row (normalizePayout —
  // backOfficeApi.js — resolves it from the destination account's own configured
  // currency) — this bank file is a REAL disbursement instruction; stamping the
  // wrong currency on it is a wrong instruction to the bank, not a display bug.
  const payoutRecords = availablePayouts.map(p => ({ id: p.id, party: p.organizer, destination: p.dest, rail: p.rail, amount: p.amount || 0, currency: p.currency || null, stage: p.stage }));

  const isEligible = (rec) => (rec.stage === 'Pending approval' || rec.stage === 'Processing');

  // Reconciliation preview is capped by the engine (~1000 rows); the export runs
  // the full query. So these totals describe the PREVIEW, not the whole export.
  const reconCapped = reconRows.length >= 1000;
  const selectedRows = source === 'payouts' ? payoutRecords.filter(r => selectedIds.has(r.id)) : reconRows;
  // A batch is realistically single-currency today (every configured payout account is
  // UGX), but the moment a KES/USD account exists, blending its total into the same
  // number as UGX would be meaningless — so this breaks the batch down BY currency, the
  // same "show each real currency honestly, never blend" fix applied to the Sales Pulse
  // ribbon and the dashboard revenue widgets. Single-currency batches render identically
  // to a plain total (one entry in the breakdown). Replaces the old single blended
  // `totalAmount`, which every display site then hardcoded a "UGX" prefix onto.
  const amountsByCurrency = source === 'payouts'
    // A bank file pays real money out. A payout row with no currency must not be summed
    // into the UGX total — that would put a foreign amount in a UGX instruction.
    ? selectedRows.reduce((acc, r) => { if (!r.currency) return acc; acc[r.currency] = (acc[r.currency] || 0) + (r.amount || 0); return acc; }, {})
    : {};
  const spansMultipleCurrencies = Object.keys(amountsByCurrency).length > 1;
  const reconAmountsByCurrency = source !== 'payouts'
    ? reconRows.reduce((acc, r) => { if (!r.currency) return acc; acc[r.currency] = (acc[r.currency] || 0) + (r.amount || 0); return acc; }, {})
    : {};
  const batchTotalLabel = source === 'payouts'
    ? (Object.entries(amountsByCurrency).length
        ? Object.entries(amountsByCurrency).map(([cur, amt]) => `${cur} ${amt.toLocaleString()}`).join(' · ')
        : 'UGX 0')
    : (Object.entries(reconAmountsByCurrency).length
        ? Object.entries(reconAmountsByCurrency).map(([cur, amt]) => `${cur} ${amt.toLocaleString()}`).join(' · ')
        : 'UGX 0');
  const totalRecords = source === 'payouts' ? selectedRows.length : reconRows.length;
  const overLimit = source === 'payouts' && totalRecords > (format?.maxPerBatch || 1000);
  const requiresApprovalAbove = 50_000_000;
  // Checked PER CURRENCY, not against the blended total — a threshold meant to catch a
  // large UGX batch means something different in KES/USD, and blending currencies first
  // would either wrongly trip the check on a mix of small batches or (worse) let a
  // genuinely large single-currency batch hide inside a blended sum that looks smaller
  // once other currencies' small amounts are netted in.
  const needsDualApproval = source === 'payouts' && (
    requireDualApproval || Object.values(amountsByCurrency).some(v => v >= requiresApprovalAbove)
  );

  // Payouts is a select→format→review wizard. Reconciliation is a single
  // pre-flight step (picker + preview) that hands off to the shared
  // ExportFlowModal — no format/review steps, no parallel exporter here.
  const STEPS = source === 'payouts'
    ? [
        { id: 'select',  label: 'Payouts' },
        { id: 'format',  label: 'Bank format' },
        { id: 'review',  label: 'Review & approve' },
      ]
    : [
        { id: 'select',  label: 'Provider reconciliation' },
      ];

  const toggleId = (id) => setSelectedIds(s => { const next = new Set(s); next.has(id) ? next.delete(id) : next.add(id); return next; });
  const selectableIds = payoutRecords.filter(isEligible).map(r => r.id);
  const allSelected = selectableIds.length > 0 && selectableIds.every(id => selectedIds.has(id));
  const toggleAll = () => setSelectedIds(() => allSelected ? new Set() : new Set(selectableIds));

  // When switching source, reset the step and selection.
  const switchSource = (next) => {
    if (next === source) return;
    setSource(next);
    setStep('select');
    setChecksum('');
    if (next === 'payouts') {
      setSelectedIds(new Set(availablePayouts.filter(p => p.stage === 'Pending approval' || p.stage === 'Processing').slice(0, 12).map(p => p.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const canSelect = source === 'payouts'
    ? (selectedRows.length > 0 && !overLimit)
    : (reconRows.length > 0);
  // Value date / bank format only apply to payouts. Reconciliation no longer
  // generates here — it hands off to ExportFlowModal — so canGenerate is
  // payouts-only.
  const canFormat = formatId && batchRef.trim() && valueDate;
  const canGenerate = canSelect && canFormat && (!needsDualApproval || approver);

  const escapeCell = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

  // Payouts bank-file body (CSV or XML, driven by the chosen bank format).
  const buildPayoutFileBody = () => {
    const rows = selectedRows.map((r, index) => ({
      sequence: index + 1,
      payoutId: r.id,
      organizer: r.party,
      destination: r.destination,
      rail: r.rail,
      amount: r.amount || 0,
      // The REAL destination-account currency, not a hardcoded assumption — this row
      // becomes a literal line in the file handed to a bank; declaring the wrong
      // currency here is a wrong disbursement instruction, not a cosmetic error.
      currency: r.currency || null,
      valueDate,
      batchRef,
      memo,
    }));
    const csv = [
      ['sequence', 'payout_id', 'organizer', 'destination', 'rail', 'amount', 'currency', 'value_date', 'batch_ref', 'memo'].join(','),
      ...rows.map(row => [row.sequence, row.payoutId, row.organizer, row.destination, row.rail, row.amount, row.currency, row.valueDate, row.batchRef, row.memo].map(escapeCell).join(',')),
    ].join('\n');
    if (format.ext === '.xml') {
      return `<PayoutBatch ref="${batchRef}" valueDate="${valueDate}">\n${rows.map(row => `  <Payment id="${row.payoutId}" amount="${row.amount}" currency="${row.currency}"><Organizer>${row.organizer}</Organizer><Destination>${row.destination}</Destination></Payment>`).join('\n')}\n</PayoutBatch>\n`;
    }
    return csv;
  };

  // Reconciliation hands off to the ONE designed export experience
  // (ExportFlowModal) — it owns the full server query, file, and ExportJob.
  // This wizard only does the pre-flight pick + preview, then launches it.
  const launchReconExport = () => {
    onReconExport({
      source: 'transactions',
      filters: reconFilters,
      presetColumns: RECON_COLUMNS.map(id => ({ id, label: id.replace(/_/g, ' '), defaultOn: true })),
      datasetLabel: `${provider.label} reconciliation`,
      from,
      to,
    });
  };

  const generate = async () => {
    // Payouts: client-built bank file with a real SHA-256 of the exact bytes.
    const body = buildPayoutFileBody();
    const filename = format.ext === '.xlsx' ? `${batchRef}.csv` : `${batchRef}${format.ext}`;
    const mime = format.ext === '.xml' ? 'application/xml' : 'text/csv;charset=utf-8';
    // REAL SHA-256 of the exact bytes we hand to the Blob.
    const digest = await sha256Hex(body);
    setChecksum(digest);
    const blob = new Blob([body], { type: mime });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
    toast('Bank file generated', 'success', `${filename} · ${totalRecords} payouts · ${batchTotalLabel} · SHA-256 ${digest.slice(0, 12)}… · download started`);
    onClose();
  };

  const modalSub = 'Batch payouts into a bank-importable file (with a real SHA-256 checksum), or export a provider reconciliation CSV — the full line-level set, run through the report engine and tracked in the Export Center — to match against a settlement statement.';

  return (
    <Modal open={true} onClose={onClose} width="max-w-[820px]" title="Generate bank file" sub={modalSub}>
      <WizardSteps steps={STEPS} current={step} />

      {step === 'select' && (
        <div className="space-y-3">
          {/* Source switcher — bank-payment batch vs provider reconciliation export */}
          <div className="inline-flex rounded-md border border-[#E6E4E1] bg-[#FBFAF7] p-0.5">
            {[
              { id: 'payouts', label: 'Payouts' },
              { id: 'reconciliation', label: 'Provider reconciliation' },
            ].map(s => (
              <button key={s.id} onClick={() => switchSource(s.id)} className={`text-[11.5px] font-semibold px-3 py-1.5 rounded ${source === s.id ? 'bg-white border border-[#1F2937] text-[#1F2937] shadow-sm' : 'text-[#6B7280] hover:text-[#1F2937]'}`}>{s.label}</button>
            ))}
          </div>

          {source === 'payouts' ? (
            <>
              <div className="grid grid-cols-4 gap-2">
                <div className="bg-[#FBFAF7] border border-[#E6E4E1] rounded-md px-3 py-2">
                  <div className="text-[10px] uppercase tracking-[0.06em] text-[#9CA3AF] font-semibold">Selected</div>
                  <div className="text-[15px] font-semibold text-[#1F2937] mt-0.5 tabular-nums">{totalRecords}</div>
                </div>
                <div className="bg-[#FBFAF7] border border-[#E6E4E1] rounded-md px-3 py-2">
                  <div className="text-[10px] uppercase tracking-[0.06em] text-[#9CA3AF] font-semibold">Batch total</div>
                  <div className="text-[15px] font-semibold text-[#1F2937] mt-0.5 tabular-nums">{batchTotalLabel}</div>
                </div>
                <div className="bg-[#FBFAF7] border border-[#E6E4E1] rounded-md px-3 py-2">
                  <div className="text-[10px] uppercase tracking-[0.06em] text-[#9CA3AF] font-semibold">Eligible</div>
                  <div className="text-[15px] font-semibold text-[#1F2937] mt-0.5 tabular-nums">{payoutRecords.filter(isEligible).length}</div>
                </div>
                <div className="bg-[#FBFAF7] border border-[#E6E4E1] rounded-md px-3 py-2">
                  <div className="text-[10px] uppercase tracking-[0.06em] text-[#9CA3AF] font-semibold">Approval</div>
                  <div className={`text-[12.5px] font-semibold mt-0.5 ${needsDualApproval ? 'text-[#C48319]' : 'text-[#1F6A3A]'}`}>{needsDualApproval ? 'Dual required' : 'Single'}</div>
                </div>
              </div>

              {overLimit && (
                <div className="bg-[#FBE9E9] border border-[#F1D2D2] rounded-md px-3 py-2 text-[11.5px] text-[#9B1F1F] flex items-start gap-2">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  <div><strong>{format.label}</strong> caps batches at {format.maxPerBatch} records. Unselect {totalRecords - format.maxPerBatch} to continue, or pick a higher-capacity format.</div>
                </div>
              )}

              {spansMultipleCurrencies && (
                <div className="bg-[#FBF3E5] border border-[#F0DEC0] rounded-md px-3 py-2 text-[11.5px] text-[#9A6A12] flex items-start gap-2">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  <div>This batch spans more than one currency ({batchTotalLabel}) — one bank file will carry mixed-currency rows. Confirm your bank format accepts a per-row currency column, or unselect down to one currency per batch.</div>
                </div>
              )}

              <div className="border border-[#E6E4E1] rounded-md overflow-x-auto max-h-[280px] overflow-y-auto">
                <table className="w-full text-[11.5px]">
                  <thead className="bg-[#FBFAF7] sticky top-0">
                    <tr className="text-[10.5px] uppercase tracking-[0.06em] text-[#6B7280]">
                      <th className="px-3 py-2 w-8"><input type="checkbox" checked={allSelected} onChange={toggleAll} /></th>
                      <th className="text-left font-medium px-3 py-2">Payout</th>
                      <th className="text-left font-medium px-3 py-2">Organizer</th>
                      <th className="text-left font-medium px-3 py-2">Destination</th>
                      <th className="text-left font-medium px-3 py-2">Stage</th>
                      <th className="text-right font-medium px-3 py-2">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payoutRecords.slice(0, 60).map(rec => {
                      const on = selectedIds.has(rec.id);
                      const eligible = isEligible(rec);
                      return (
                        <tr key={rec.id} className={`border-t border-[#EEEDEA] ${on ? 'bg-[#FBFAF7]' : 'hover:bg-[#FBFAF7]'} ${!eligible ? 'opacity-60' : ''}`}>
                          <td className="px-3 py-2"><input type="checkbox" checked={on} disabled={!eligible} onChange={() => toggleId(rec.id)} /></td>
                          <td className="px-3 py-2 font-mono text-[#1F2937]"><IdTag value={rec.id} /></td>
                          <td className="px-3 py-2 text-[#374151]">{rec.party}</td>
                          <td className="px-3 py-2 text-[#6B7280]">{rec.destination}</td>
                          <td className="px-3 py-2"><StatusDot status={rec.stage} /></td>
                          <td className="px-3 py-2 text-right tabular-nums font-medium text-[#1F2937]">{rec.currency ? `${rec.currency} ${(rec.amount || 0).toLocaleString()}` : '—'}</td>
                        </tr>
                      );
                    })}
                    {payoutRecords.length === 0 && (
                      <tr><td colSpan={6} className="px-3 py-8 text-center text-[12px] text-[#9CA3AF]">No eligible payouts to batch</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="text-[11px] text-[#6B7280]">Only payouts in <strong>Pending approval</strong> or <strong>Processing</strong> can be batched. Held, failed, and paid records are filtered.</div>
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Provider" sub="Hand this export to the provider to reconcile against their settlement statement">
                  <Select value={providerKey} onChange={e => setProviderKey(e.target.value)}>
                    {RECON_PROVIDERS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                  </Select>
                </Field>
                <Field label="Status" sub="successful = settled transactions to match against the provider's statement; all = include pending/failed to chase discrepancies">
                  <Select value={reconStatus} onChange={e => setReconStatus(e.target.value)}>
                    <option value="successful">Successful</option>
                    <option value="all">All</option>
                  </Select>
                </Field>
                <Field label="From" sub="Window start (inclusive)"><Input type="date" value={from} onChange={e => setFrom(e.target.value)} /></Field>
                <Field label="To" sub="Window end (inclusive)"><Input type="date" value={to} onChange={e => setTo(e.target.value)} /></Field>
              </div>

              <div className="grid grid-cols-4 gap-2">
                <div className="bg-[#FBFAF7] border border-[#E6E4E1] rounded-md px-3 py-2">
                  <div className="text-[10px] uppercase tracking-[0.06em] text-[#9CA3AF] font-semibold">In preview</div>
                  <div className="text-[15px] font-semibold text-[#1F2937] mt-0.5 tabular-nums">{reconLoading ? '—' : `${totalRecords.toLocaleString()}${reconCapped ? '+' : ''}`}</div>
                </div>
                <div className="bg-[#FBFAF7] border border-[#E6E4E1] rounded-md px-3 py-2">
                  <div className="text-[10px] uppercase tracking-[0.06em] text-[#9CA3AF] font-semibold">Preview amount</div>
                  <div className="text-[15px] font-semibold text-[#1F2937] mt-0.5 tabular-nums">{reconLoading ? '—' : batchTotalLabel}</div>
                </div>
                <div className="bg-[#FBFAF7] border border-[#E6E4E1] rounded-md px-3 py-2">
                  <div className="text-[10px] uppercase tracking-[0.06em] text-[#9CA3AF] font-semibold">Provider</div>
                  <div className="text-[12.5px] font-semibold text-[#1F2937] mt-0.5">{provider.label}</div>
                </div>
                <div className="bg-[#FBFAF7] border border-[#E6E4E1] rounded-md px-3 py-2">
                  <div className="text-[10px] uppercase tracking-[0.06em] text-[#9CA3AF] font-semibold">Window</div>
                  <div className="text-[11px] font-semibold text-[#1F2937] mt-0.5 tabular-nums">{from} → {to}</div>
                </div>
              </div>

              {reconCapped && (
                <div className="bg-[#FBF3E5] border border-[#F0DEC0] rounded-md px-3 py-2 text-[11.5px] text-[#9A6A12] flex items-start gap-2">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  <div>Preview capped at 1,000 rows — the export still includes ALL matching transactions.</div>
                </div>
              )}

              <div className="border border-[#E6E4E1] rounded-md overflow-x-auto max-h-[280px] overflow-y-auto">
                <table className="w-full text-[11.5px]">
                  <thead className="bg-[#FBFAF7] sticky top-0">
                    <tr className="text-[10.5px] uppercase tracking-[0.06em] text-[#6B7280]">
                      <th className="text-left font-medium px-3 py-2">Date</th>
                      <th className="text-left font-medium px-3 py-2">MSISDN</th>
                      <th className="text-left font-medium px-3 py-2">Customer</th>
                      <th className="text-right font-medium px-3 py-2">Amount</th>
                      <th className="text-left font-medium px-3 py-2">{BRAND.name} ref</th>
                      <th className="text-left font-medium px-3 py-2">Gateway ref</th>
                      <th className="text-left font-medium px-3 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!reconLoading && !reconError && reconRows.slice(0, 30).map((r, i) => (
                      <tr key={`${r.tkt_ref || r.gateway_ref || i}-${i}`} className="border-t border-[#EEEDEA] hover:bg-[#FBFAF7]">
                        <td className="px-3 py-2 text-[#374151] tabular-nums whitespace-nowrap">{r.date}</td>
                        <td className="px-3 py-2 font-mono text-[10.5px] text-[#374151]">{r.msisdn}</td>
                        <td className="px-3 py-2 text-[#374151]">{r.customer}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-medium text-[#1F2937]">{r.currency ? `${r.currency} ${(r.amount || 0).toLocaleString()}` : '—'}</td>
                        <td className="px-3 py-2 font-mono text-[10.5px] text-[#9CA3AF]">{r.tkt_ref}</td>
                        <td className="px-3 py-2 font-mono text-[10.5px] text-[#9CA3AF]">{r.gateway_ref}</td>
                        <td className="px-3 py-2"><StatusDot status={r.status} /></td>
                      </tr>
                    ))}
                    {reconLoading && (
                      <tr><td colSpan={7} className="px-3 py-8 text-center text-[12px] text-[#9CA3AF]">Loading transactions…</td></tr>
                    )}
                    {!reconLoading && reconError && (
                      <tr><td colSpan={7} className="px-3 py-8 text-center text-[12px] text-[#9B1F1F]">{reconError}</td></tr>
                    )}
                    {!reconLoading && !reconError && reconRows.length === 0 && (
                      <tr><td colSpan={7} className="px-3 py-8 text-center text-[12px] text-[#9CA3AF]">No transactions for this provider in this window</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {!reconLoading && !reconError && reconRows.length > 0 && (
                <div className="text-[11px] text-[#6B7280]">Showing first {Math.min(30, reconRows.length)} of {totalRecords.toLocaleString()}{reconCapped ? '+' : ''} in preview — the export includes ALL matching rows.</div>
              )}
            </>
          )}
        </div>
      )}

      {step === 'format' && (
        <div className="space-y-3">
          <Field label="Bank file format" required>
            <div className="space-y-1.5">
              {BANK_FILE_FORMATS.map(f => (
                <label key={f.id} className={`flex items-start gap-2.5 px-3 py-2.5 rounded-md border cursor-pointer ${formatId === f.id ? 'border-[#1F2937] bg-[#FBFAF7]' : 'border-[#E6E4E1] hover:bg-[#FBFAF7]'}`}>
                  <input type="radio" name="bankformat" checked={formatId === f.id} onChange={() => setFormatId(f.id)} className="mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2"><span className="text-[12.5px] font-semibold text-[#1F2937]">{f.label}</span><span className="text-[10.5px] font-mono text-[#9CA3AF]">{f.ext}</span></div>
                    <div className="text-[10.5px] text-[#6B7280] mt-0.5 leading-snug">{f.sub}</div>
                    <div className="text-[10px] text-[#9CA3AF] mt-1">Up to {f.maxPerBatch.toLocaleString()} records · {f.banks.join(', ')}</div>
                  </div>
                </label>
              ))}
            </div>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Value date" required sub="When the bank should execute the payments"><Input type="date" value={valueDate} onChange={e => setValueDate(e.target.value)} /></Field>
            <Field label="Batch reference" required sub="Used by the bank to acknowledge the batch"><Input value={batchRef} onChange={e => setBatchRef(e.target.value)} placeholder="BAT-YYYYMMDD-XXX" /></Field>
          </div>

          <Field label="Memo / narration" optional sub="Appears in the bank's reporting to recipients (max 70 chars)">
            <Input value={memo} onChange={e => setMemo(e.target.value.slice(0, 70))} placeholder="TKT Payout · May 2026" />
          </Field>

          <div className="space-y-1.5">
            <ToggleControl value={includeFees} onChange={setIncludeFees} label={`Include ${BRAND.name} platform fees on a separate line`} sub={`When on, organizer net + ${BRAND.name} fee = gross transfer · for tax invoicing`} />
            <ToggleControl value={requireDualApproval} onChange={setRequireDualApproval} label="Require dual approval before download" sub="Standard for any single currency's batch above 50M · cannot be turned off if a threshold is met" locked={Object.values(amountsByCurrency).some(v => v >= requiresApprovalAbove)} />
          </div>

          <Field label="File encryption" sub="A flag recorded on the batch — encryption is applied at hand-off to the bank, not in this local download">
            <div className="grid grid-cols-3 gap-2">
              {[
                { id: 'pgp',  label: 'PGP encrypted',     sub: 'Recommended · bank public key' },
                { id: 'zip',  label: 'Password-protected ZIP', sub: 'Password sent via SMS' },
                { id: 'none', label: 'Plain (no encryption)',  sub: 'Internal use only' },
              ].map(e => (
                <button key={e.id} onClick={() => setEncryption(e.id)} className={`text-left rounded-md border px-3 py-2 ${encryption === e.id ? 'border-[#1F2937] bg-[#FBFAF7]' : 'border-[#E6E4E1] hover:bg-[#FBFAF7]'}`}>
                  <div className="text-[11.5px] font-semibold text-[#1F2937]">{e.label}</div>
                  <div className="text-[10px] text-[#6B7280] mt-0.5">{e.sub}</div>
                </button>
              ))}
            </div>
          </Field>
        </div>
      )}

      {step === 'review' && source === 'payouts' && (
        <div className="space-y-3">
          <div className="bg-[#FBFAF7] border border-[#E6E4E1] rounded-md p-3">
            <div className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-[#6B7280] mb-2">Batch summary</div>
            <dl className="grid grid-cols-[180px_1fr] gap-y-1.5 text-[11.5px]">
              <dt className="text-[#6B7280]">Source</dt><dd className="text-[#1F2937] font-medium">Payouts</dd>
              <dt className="text-[#6B7280]">Format</dt><dd className="text-[#1F2937] font-medium">{format.label} <span className="font-mono text-[10.5px] text-[#9CA3AF]">{format.ext}</span></dd>
              <dt className="text-[#6B7280]">Batch reference</dt><dd className="text-[#1F2937] font-mono">{batchRef}</dd>
              <dt className="text-[#6B7280]">Value date</dt><dd className="text-[#1F2937]">{valueDate || '—'}</dd>
              <dt className="text-[#6B7280]">Memo</dt><dd className="text-[#1F2937]">{memo || <span className="text-[#9CA3AF]">— none —</span>}</dd>
              <dt className="text-[#6B7280]">Records</dt><dd className="text-[#1F2937] tabular-nums">{totalRecords}</dd>
              <dt className="text-[#6B7280]">Total amount</dt><dd className="text-[#1F2937] font-semibold tabular-nums">{batchTotalLabel}</dd>
              <dt className="text-[#6B7280]">Encryption flag</dt><dd className="text-[#1F2937]">{encryption === 'pgp' ? 'PGP encrypted' : encryption === 'zip' ? 'Password ZIP' : 'Plain (no encryption)'} <span className="text-[#9CA3AF]">· applied at bank hand-off</span></dd>
              <dt className="text-[#6B7280]">Checksum</dt><dd className="font-mono text-[10.5px] text-[#1F2937] break-all">{checksum ? checksum : <span className="text-[#9CA3AF] font-sans">SHA-256 · computed from the file bytes on generate</span>}</dd>
            </dl>
          </div>

          {needsDualApproval && (
            <Field label="Second approver" required sub="A note recorded with the batch. Approval routing and audit are not yet wired to a backend — this does not block the download.">
              <Select value={approver} onChange={e => setApprover(e.target.value)}>
                <option value="">Choose approver…</option>
                {['Victor S. (Ops lead)','Lillian K. (Finance lead)','Esther N. (Risk lead)','Brian O. (Account mgr)'].map(o => <option key={o}>{o}</option>)}
              </Select>
            </Field>
          )}

          <div className="bg-[#F0F9F2] border border-[#C4E0CC] rounded-md p-3">
            <div className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-[#1F6A3A] mb-2">On generate</div>
            <ul className="text-[11.5px] text-[#1F6A3A] space-y-1 list-disc list-inside">
              <li>Generates a bank-importable <span className="font-medium">{format.label}</span> file <span className="font-mono">{format.ext === '.xlsx' ? `${batchRef}.csv` : `${batchRef}${format.ext}`}</span> and downloads it</li>
              <li>A real SHA-256 checksum of the exact file bytes is shown so you can verify the file with the bank</li>
              {needsDualApproval && <li>Flagged for second approval{approver ? <> (<span className="font-medium">{approver}</span>)</> : ''} — note: approval routing and audit are not yet wired to a backend</li>}
            </ul>
          </div>
        </div>
      )}

      <WizardFooter
        onBack={step !== 'select' ? () => setStep('format') : null}
        onCancel={onClose}
        onNext={() => {
          // Reconciliation is a single step → hand off to ExportFlowModal.
          if (source === 'reconciliation') {
            if (canSelect) launchReconExport();
            return;
          }
          if (step === 'select') {
            if (!canSelect) return;
            setStep('format');
          } else if (step === 'format') {
            if (canFormat) setStep('review');
          } else {
            if (canGenerate) generate();
          }
        }}
        nextLabel={source === 'reconciliation'
          ? 'Export reconciliation →'
          : (step === 'review' ? (needsDualApproval ? 'Generate & route for approval' : 'Generate & download') : 'Continue')}
        nextDisabled={source === 'reconciliation' ? !canSelect : (step === 'select' ? !canSelect : step === 'format' ? !canFormat : !canGenerate)}
        primaryIcon={(source === 'reconciliation' || step === 'review') ? Download : null}
      />
    </Modal>
  );
};

/* ----------------------------------------------------------------------------
   NEW CASE WIZARD — Compliance/risk case management
   Captures subject, type, severity, linked entity, owner, evidence, SLA, watchers
   ---------------------------------------------------------------------------- */
